import PageHeader from "@/components/page-header";
import { CheckPaymentSuccessDialog } from "@/components/registrant/check-payment-success-dialog";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { AddressDetailsForm } from "@/components/registrant/license-form/address-details";
import { AddressLookupVerificationForm } from "@/components/registrant/license-form/address-lookup-verification";
import { LicenseTypeQuestionsForm } from "@/components/registrant/license-form/license-type-questions";
import { PeriodsAndFeesForm } from "@/components/registrant/license-form/periods-and-fees";
import {
  TransferExistingLicensePrompt,
  type TransferableLicenseOption,
} from "@/components/registrant/transfer-existing-license-prompt";
import { TransferOutstandingResponsibility } from "@/components/registrant/transfer-outstanding-responsibility";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  getLicenseTypesOptions,
  getRegistrantUnregisteredPropertiesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  JurisdictionResponseSchema,
  LicenseTypeResponseSchema,
  UnregisteredPropertyResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseFormData } from "@/hooks/use-license-form-data";
import { useLicenseFormMutations } from "@/hooks/use-license-form-mutations";
import { useStepManager } from "@/hooks/use-step-manager";
import { AUTO_GENERATED_QUESTIONS } from "@/lib/auto-generated-questions";
import { filterAvailableLicenseTypes } from "@/lib/license-type-utils";
import { invalidateRegistrantDashboardLists } from "@/lib/registrant-dashboard-query-invalidation";
import {
  unregisteredPropertyAddressPrefill,
  unregisteredRegistrationAnswersPrefill,
} from "@/lib/registrant-unregistered-prefill";
import { getRegistrationTaggedQuestion } from "@/lib/service-transfer-wizard";
import { partyIdFromUnregisteredAnswer } from "@/lib/unregistered-registration-answers";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import type { AddressDetails, ValidAddressDetails } from "@/types/address";
import type { LicenseAnswers, PeriodsAndFeesData } from "@/types/license";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/registrant/new-license")({
  component: NewLicenseScreen,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    licenseTypeId?: string;
    jurisdictionId?: string;
    unregisteredId?: string;
  } => {
    return {
      licenseTypeId: (search.licenseTypeId as string) || undefined,
      jurisdictionId: (search.jurisdictionId as string) || undefined,
      unregisteredId: (search.unregisteredId as string) || undefined,
    };
  },
});

enum NewLicenseStep {
  AddressDetails = 1, // Only for license types that require an property/address
  AddressLookupVerification = 2, // Only for license types that require a property/address
  ExistingRegistration = 3,
  LicenseTypeQuestions = 4,
  PeriodsAndFees = 5,
}

export function NewLicenseScreen() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const queryClient = useQueryClient();
  const {
    licenseTypeId: searchLicenseTypeId,
    jurisdictionId: searchJurisdictionId,
    unregisteredId: searchUnregisteredId,
  } = Route.useSearch();
  const navigate = useNavigate();
  const [licenseType, setLicenseType] =
    useState<LicenseTypeResponseSchema | null>(null);
  const [propertyAddress, setPropertyAddress] = useState<AddressDetails | null>(
    null,
  );
  const [geocodedAddress, setGeocodedAddress] =
    useState<ValidAddressDetails | null>(null);
  const [apn, setApn] = useState<string | null>(null);
  const [geocodedLatitude, setGeocodedLatitude] = useState<number | null>(null);
  const [geocodedLongitude, setGeocodedLongitude] = useState<number | null>(
    null,
  );
  const [jurisdiction, setJurisdiction] =
    useState<JurisdictionResponseSchema | null>(null);
  const [licenseAnswers, setLicenseAnswers] = useState<LicenseAnswers | null>(
    null,
  );
  const [transferFromLicenseId, setTransferFromLicenseId] = useState<
    string | null
  >(null);
  const [transferEffectiveDate, setTransferEffectiveDate] = useState<
    string | null
  >(null);
  const [transferOriginalOmtAnswer, setTransferOriginalOmtAnswer] = useState<
    unknown | null
  >(null);
  const [transferPassPriorUnpaid, setTransferPassPriorUnpaid] = useState<
    boolean | null
  >(null);
  const [existingRegistrationMatchFound, setExistingRegistrationMatchFound] =
    useState<boolean | null>(null);
  const [step, setStep] = useState<NewLicenseStep | null>(null);
  const [checkPaymentSuccess, setCheckPaymentSuccess] = useState<{
    displayId: string;
    licenseId: string;
    checkMailingAddress?: string | null;
    variant: "check" | "invoice";
  } | null>(null);
  const initializedUnregisteredIdRef = useRef<string | null>(null);
  const appliedOwnerPrefillIdRef = useRef<string | null>(null);

  const { stepNumber, nextStep, previousStep, goToStep } = useStepManager({
    maxSteps: 5,
  });

  // Get license types
  const {
    data: licenseTypesData,
    isLoading: areLicenseTypesLoading,
    error: licenseTypesQueryError,
  } = useQuery({
    ...getLicenseTypesOptions(),
    enabled: isAuthSetupComplete,
  });

  const {
    data: registrantUnregisteredData,
    isLoading: isRegistrantUnregisteredLoading,
    error: registrantUnregisteredError,
  } = useQuery({
    ...getRegistrantUnregisteredPropertiesOptions({
      query: {
        unregistered_id: searchUnregisteredId,
        page_size: 1,
      },
    }),
    enabled: isAuthSetupComplete && !!searchUnregisteredId,
  });

  const sourceUnregistered: UnregisteredPropertyResponseSchema | undefined =
    searchUnregisteredId
      ? registrantUnregisteredData?.unregistered.find(
          (row) => row.id === searchUnregisteredId,
        )
      : undefined;

  const sourceUnregisteredLicenseType = sourceUnregistered
    ? licenseTypesData?.license_types.find(
        (type) => type.id === sourceUnregistered.license_type.id,
      )
    : undefined;

  useEffect(() => {
    if (
      !searchUnregisteredId ||
      !sourceUnregistered ||
      !sourceUnregisteredLicenseType ||
      initializedUnregisteredIdRef.current === searchUnregisteredId
    ) {
      return;
    }
    setLicenseType(sourceUnregisteredLicenseType);
    setJurisdiction(sourceUnregistered.jurisdiction);
    setPropertyAddress(unregisteredPropertyAddressPrefill(sourceUnregistered));
    setLicenseAnswers(
      unregisteredRegistrationAnswersPrefill(sourceUnregistered),
    );
    setStep(
      sourceUnregisteredLicenseType.requires_property
        ? NewLicenseStep.AddressDetails
        : NewLicenseStep.LicenseTypeQuestions,
    );
    initializedUnregisteredIdRef.current = searchUnregisteredId;
  }, [searchUnregisteredId, sourceUnregistered, sourceUnregisteredLicenseType]);

  // Always fetch configuration/questions as soon as possible
  const {
    configurationData,
    questionsData,
    isLoading: isConfigLoading,
    error: configError,
  } = useLicenseFormData({
    licenseTypeId: licenseType?.id,
    jurisdictionId: jurisdiction?.id,
    isRenewal: false,
  });

  useEffect(() => {
    if (
      !searchUnregisteredId ||
      !sourceUnregistered ||
      !questionsData?.questions ||
      appliedOwnerPrefillIdRef.current === searchUnregisteredId
    ) {
      return;
    }
    const prefill = unregisteredRegistrationAnswersPrefill(
      sourceUnregistered,
      questionsData.questions,
    );
    setLicenseAnswers((current) => ({
      ...prefill,
      ...(current ?? {}),
    }));
    appliedOwnerPrefillIdRef.current = searchUnregisteredId;
  }, [questionsData?.questions, searchUnregisteredId, sourceUnregistered]);

  // Only fetch fees after property structure answer is available
  const propertyStructureAnswer = (() => {
    if (!questionsData || !licenseAnswers) return undefined;
    const propertyStructureQuestion = questionsData.questions?.find(
      (q) =>
        q.question.trim().toLowerCase() === "what is the property structure?" &&
        q.answer_type === "single_select",
    );
    if (!propertyStructureQuestion) return undefined;
    const answer = licenseAnswers[propertyStructureQuestion.id];
    return typeof answer === "string" ? answer : undefined;
  })();

  // Only fetch fees after commercial managed property answer is available
  const commercialManagedPropertyAnswer = (() => {
    if (!questionsData || !licenseAnswers) return undefined;
    const commercialManagedPropertyQuestion = questionsData.questions?.find(
      (q) =>
        q.question.trim().toLowerCase() ===
          "is this a commercial property under contract with property management services?" &&
        q.answer_type === "boolean",
    );
    if (!commercialManagedPropertyQuestion) return undefined;
    const answer = licenseAnswers[commercialManagedPropertyQuestion.id];
    return typeof answer === "boolean" ? answer : undefined;
  })();

  const numberOfUnitsAnswer = (() => {
    if (!questionsData || !licenseAnswers) return undefined;
    const numberOfUnitsQuestion = questionsData.questions?.find(
      (q) =>
        q.question.trim().toLowerCase() ===
          AUTO_GENERATED_QUESTIONS.number_of_units.text.trim().toLowerCase() &&
        q.answer_type === "number",
    );
    if (!numberOfUnitsQuestion) return undefined;
    const answer = licenseAnswers[numberOfUnitsQuestion.id];
    if (typeof answer === "number") {
      return answer;
    }
    if (typeof answer === "string" && answer.trim() !== "") {
      const parsed = Number(answer);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  })();

  const vacancyAnswer = (() => {
    if (!questionsData || !licenseAnswers) return undefined;
    const vacancyQuestion = questionsData.questions?.find(
      (q) =>
        q.question.trim().toLowerCase() ===
          AUTO_GENERATED_QUESTIONS.vacancy.text.trim().toLowerCase() &&
        q.answer_type === "boolean",
    );
    if (!vacancyQuestion) return undefined;
    const answer = licenseAnswers[vacancyQuestion.id];
    return typeof answer === "boolean" ? answer : undefined;
  })();

  const inspectionExemptionAnswer = (() => {
    if (!questionsData || !licenseAnswers) return undefined;
    const inspectionExemptionQuestion = questionsData.questions?.find(
      (q) =>
        q.question.trim().toLowerCase() ===
          AUTO_GENERATED_QUESTIONS.inspection_exemption.text
            .trim()
            .toLowerCase() && q.answer_type === "date",
    );
    if (!inspectionExemptionQuestion) return undefined;
    const answer = licenseAnswers[inspectionExemptionQuestion.id];
    if (typeof answer === "string" && answer.trim() !== "") {
      return answer; // Date string in YYYY-MM-DD format
    }
    return undefined;
  })();

  const omtPartyId = (() => {
    if (!questionsData || !licenseAnswers) return null;
    const omtQuestion = getRegistrationTaggedQuestion(
      questionsData.questions,
      "OMT",
    );
    return omtQuestion
      ? partyIdFromUnregisteredAnswer(licenseAnswers[omtQuestion.id])
      : null;
  })();

  const { feesData } = useLicenseFormData({
    licenseTypeId: licenseType?.id,
    jurisdictionId: jurisdiction?.id,
    effectiveDate: licenseAnswers?.effective_date,
    isRenewal: false,
    propertyStructureAnswer,
    commercialManagedPropertyAnswer,
    numberOfUnitsAnswer,
    vacancyAnswer,
    inspectionExemptionAnswer,
    omtPartyId,
  });

  // Use custom mutations hook
  const {
    handleCreateLicense,
    handleCreateCheckPayment,
    handleCreateInvoicePayment,
    handleMakePayment,
    processLicenseAnswers,
  } = useLicenseFormMutations({
    onSuccess: async (_licenseId) => {
      // Payment will be handled in handleSubmit with the correct payment method type
    },
  });

  // Handle license type selection
  const handleLicenseTypeSelection = (
    licenseType: LicenseTypeResponseSchema,
  ) => {
    setLicenseType(licenseType);
    if (licenseType.requires_property) {
      setStep(NewLicenseStep.AddressDetails);
    } else {
      // TODO: Need some logic to get the jurisdiction id for non-property licenses
      setStep(NewLicenseStep.LicenseTypeQuestions);
    }
  };

  // Handle step navigation
  const handleNextStep = async () => {
    if (step === NewLicenseStep.AddressDetails) {
      setStep(NewLicenseStep.AddressLookupVerification);
    } else if (step === NewLicenseStep.AddressLookupVerification) {
      setStep(NewLicenseStep.ExistingRegistration);
    } else if (step === NewLicenseStep.ExistingRegistration) {
      setStep(NewLicenseStep.LicenseTypeQuestions);
    } else if (step === NewLicenseStep.LicenseTypeQuestions) {
      setStep(NewLicenseStep.PeriodsAndFees);
    }
    nextStep();
  };

  const handlePreviousStep = async () => {
    if (step === NewLicenseStep.AddressDetails) {
      setStep(NewLicenseStep.AddressDetails);
    } else if (step === NewLicenseStep.AddressLookupVerification) {
      setStep(NewLicenseStep.AddressDetails);
    } else if (step === NewLicenseStep.ExistingRegistration) {
      setStep(NewLicenseStep.AddressLookupVerification);
    } else if (step === NewLicenseStep.LicenseTypeQuestions) {
      if (licenseType?.requires_property) {
        setStep(
          existingRegistrationMatchFound
            ? NewLicenseStep.ExistingRegistration
            : NewLicenseStep.AddressLookupVerification,
        );
      } else {
        setStep(NewLicenseStep.AddressDetails);
      }
    } else if (step === NewLicenseStep.PeriodsAndFees) {
      setStep(NewLicenseStep.LicenseTypeQuestions);
    }
    previousStep();
  };

  const selectTransferLicense = (license: TransferableLicenseOption | null) => {
    if (!license) {
      setTransferFromLicenseId(null);
      setTransferEffectiveDate(null);
      setTransferOriginalOmtAnswer(null);
      setTransferPassPriorUnpaid(null);
      return;
    }
    setTransferFromLicenseId(license.license_id);
    setTransferEffectiveDate(license.effective_date);
    setTransferPassPriorUnpaid(null);
    const prefill: Record<string, unknown> = {};
    for (const item of license.answers_to_additional_questions ?? []) {
      prefill[item.question_id] = item.answer;
    }
    const omtQuestion = getRegistrationTaggedQuestion(
      questionsData?.questions,
      "OMT",
    );
    setTransferOriginalOmtAnswer(
      omtQuestion ? (prefill[omtQuestion.id] ?? null) : null,
    );
    setLicenseAnswers(
      (previous) =>
        ({
          ...(previous ?? {}),
          ...prefill,
          effective_date: license.effective_date,
        }) as LicenseAnswers,
    );
  };

  // Handle form submission
  const handleSubmit = async (
    data: PeriodsAndFeesData,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => {
    if (
      !licenseType ||
      !jurisdiction ||
      !geocodedAddress ||
      !licenseAnswers ||
      !geocodedLatitude ||
      !geocodedLongitude
    ) {
      console.error("Missing required data for license creation");
      return;
    }

    const answers = processLicenseAnswers({
      ...licenseAnswers,
      ...(data.attestation_answers ?? {}),
    });

    try {
      const license = await handleCreateLicense({
        pay_later_bulk_cohort: paymentMethodType === "pay_later",
        license_type_id: licenseType.id,
        jurisdiction_id: jurisdiction.id,
        property: {
          jurisdiction_id: jurisdiction.id,
          address1: geocodedAddress.address1,
          address2: geocodedAddress.address2,
          city: geocodedAddress.city,
          state: geocodedAddress.state,
          zipcode: geocodedAddress.zipcode,
          latitude: geocodedLatitude,
          longitude: geocodedLongitude,
          apn: apn,
        },
        effective_date: transferEffectiveDate ?? licenseAnswers.effective_date,
        answers_to_additional_questions: answers,
        periods: data.periods,
        registrant_note: data.registrant_note,
        ...(transferFromLicenseId
          ? {
              transfer_from_license_id: transferFromLicenseId,
              // Server resolves OMT-unchanged → pass; CHANGE → Path A;
              // UPDATE uses this flag when OMT changed.
              pass_prior_unpaid_responsibility: transferPassPriorUnpaid ?? true,
              enact_omt_change:
                configurationData?.omt === "change" ? true : undefined,
            }
          : {}),
      } as Parameters<typeof handleCreateLicense>[0]);

      // Transfer builds its own period/payment rows; skip new-registration fee checkout.
      if (transferFromLicenseId) {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: license.license_id },
        });
        return;
      }

      // If "Pay Later" is selected, navigate to license details instead of payment
      if (paymentMethodType === "pay_later") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: license.license_id },
        });
        return;
      }

      if (paymentMethodType === "no_payment") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: license.license_id },
        });
        return;
      }

      if (paymentMethodType === "check") {
        const result = await handleCreateCheckPayment(
          license.license_id,
          data.periods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          licenseId: license.license_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "check",
        });
        return;
      }

      if (paymentMethodType === "invoice") {
        const result = await handleCreateInvoicePayment(
          license.license_id,
          data.periods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          licenseId: license.license_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "invoice",
        });
        return;
      }

      if (
        paymentMethodType === "card" ||
        paymentMethodType === "us_bank_account"
      ) {
        // Make payment using the pending records created by handleCreateLicense
        await handleMakePayment(license.license_id, paymentMethodType);
      }
    } catch (e) {
      void invalidateRegistrantDashboardLists(queryClient);
      throw e;
    }
  };

  // Early returns for loading and error states
  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (
    areLicenseTypesLoading ||
    isConfigLoading ||
    (searchUnregisteredId && isRegistrantUnregisteredLoading)
  ) {
    return <LoadingScreen message="Fetching data..." />;
  }

  const loadingError =
    licenseTypesQueryError || configError || registrantUnregisteredError;
  if (loadingError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(loadingError)}
        message={getQueryErrorMessage(loadingError)}
      />
    );
  }

  if (
    searchUnregisteredId &&
    registrantUnregisteredData &&
    (!sourceUnregistered || !sourceUnregisteredLicenseType)
  ) {
    return (
      <ErrorComponent
        title="Unregistered property not found"
        message="This unregistered property is no longer available to register."
      />
    );
  }

  if (
    searchUnregisteredId &&
    sourceUnregistered &&
    initializedUnregisteredIdRef.current !== searchUnregisteredId
  ) {
    return <LoadingScreen message="Preparing registration..." />;
  }

  // Filter out license types that are under development or deleted
  const licenseTypes = filterAvailableLicenseTypes(
    licenseTypesData?.license_types,
  );
  if (!licenseTypes || licenseTypes.length === 0) {
    return (
      <ErrorComponent
        title="No License Types Available"
        message="There are currently no license types available for registration. Please contact support for assistance."
      />
    );
  }

  if (step === null) {
    // Initial load
    if (searchLicenseTypeId) {
      // If license type ID is provided in search params, use it directly
      const selectedLicenseType = licenseTypes.find(
        (lt) => lt.id === searchLicenseTypeId,
      );
      if (selectedLicenseType) {
        handleLicenseTypeSelection(selectedLicenseType);
        // State updates are async; avoid rendering header with no form for this frame
        return <LoadingScreen message="Loading..." />;
      }
      // Invalid license type ID, redirect back to dashboard
      window.location.href = "/registrant/dashboard";
      return;
    }
    if (licenseTypes.length === 1) {
      // If there is only one license type, we can skip the selection step
      handleLicenseTypeSelection(licenseTypes[0]);
      return <LoadingScreen message="Loading..." />;
    }
    // Multiple license types but no specific one selected, redirect to dashboard
    window.location.href = "/registrant/dashboard";
    return;
  }

  // Non-property license types jump to LicenseTypeQuestions but jurisdiction is only set
  // after address verification, so config/questions never load. Show a clear error instead of blank.
  if (
    step === NewLicenseStep.LicenseTypeQuestions &&
    licenseType &&
    !licenseType.requires_property &&
    !jurisdiction
  ) {
    return (
      <ErrorComponent
        title="Registration not available"
        message="This registration type is not fully set up for online registration yet. Please contact support for assistance."
      />
    );
  }

  // LicenseTypeQuestions step but form data not loaded yet (e.g. still loading) or missing
  if (
    step === NewLicenseStep.LicenseTypeQuestions &&
    licenseType &&
    !questionsData
  ) {
    return <LoadingScreen message="Loading registration form..." />;
  }

  return (
    <div
      data-slot="registration-form-scroll"
      className="flex min-h-0 flex-1 flex-col overflow-y-auto container mx-auto max-w-3xl"
    >
      <CheckPaymentSuccessDialog
        isOpen={!!checkPaymentSuccess}
        onOpenChange={(open) => {
          if (!open) setCheckPaymentSuccess(null);
        }}
        displayId={checkPaymentSuccess?.displayId ?? ""}
        checkMailingAddress={checkPaymentSuccess?.checkMailingAddress}
        variant={checkPaymentSuccess?.variant ?? "check"}
        onOk={() => {
          if (checkPaymentSuccess) {
            navigate({
              to: "/registrant/licenses/$licenseId/view",
              params: { licenseId: checkPaymentSuccess.licenseId },
            });
          }
        }}
      />
      <PageHeader
        title={
          licenseType ? `Register ${licenseType.name}` : "Register Property"
        }
        subtitle={
          licenseType
            ? `Complete the form below to register a ${licenseType.name.toLowerCase()}`
            : "Complete the form below to register a property"
        }
        className="mb-6"
        action={<ContextHelpButton articleId="register-a-property" />}
      />

      {step === NewLicenseStep.AddressDetails && (
        <AddressDetailsForm
          onPrevious={async () => {
            // Go back to dashboard since we can't go back to license type selection
            window.location.href = "/registrant/dashboard";
          }}
          onNext={async (addressDetails) => {
            setPropertyAddress(addressDetails);
            setStep(NewLicenseStep.AddressLookupVerification);
            handleNextStep();
          }}
          stepNumber={stepNumber}
          initialData={propertyAddress ?? undefined}
          configurationData={configurationData ?? undefined}
        />
      )}

      {step === NewLicenseStep.AddressLookupVerification && propertyAddress && (
        <AddressLookupVerificationForm
          addressDetails={propertyAddress}
          expectedJurisdictionId={searchJurisdictionId}
          onPrevious={async () => {
            setStep(NewLicenseStep.AddressDetails);
            handlePreviousStep();
          }}
          onNext={async ({
            jurisdiction,
            apn,
            geocoded_address1,
            geocoded_address2,
            geocoded_city,
            geocoded_state,
            geocoded_zipcode,
            geocoded_lat,
            geocoded_lng,
          }) => {
            setJurisdiction(jurisdiction);
            setApn(apn);
            setGeocodedAddress({
              address1: geocoded_address1,
              address2: geocoded_address2,
              city: geocoded_city,
              state: geocoded_state,
              zipcode: geocoded_zipcode,
            });
            setGeocodedLatitude(geocoded_lat);
            setGeocodedLongitude(geocoded_lng);
            setExistingRegistrationMatchFound(null);
            setStep(NewLicenseStep.ExistingRegistration);
            handleNextStep();
          }}
          stepNumber={stepNumber}
          licenseTypeId={licenseType?.id}
        />
      )}

      {step === NewLicenseStep.ExistingRegistration && jurisdiction ? (
        <TransferExistingLicensePrompt
          jurisdictionId={jurisdiction.id}
          apn={apn}
          address1={geocodedAddress?.address1 ?? null}
          address2={geocodedAddress?.address2 ?? null}
          city={geocodedAddress?.city ?? null}
          state={geocodedAddress?.state ?? null}
          zipcode={geocodedAddress?.zipcode ?? null}
          latitude={geocodedLatitude}
          longitude={geocodedLongitude}
          selectedTransferLicenseId={transferFromLicenseId}
          stepNumber={stepNumber}
          onPrevious={() => {
            setStep(NewLicenseStep.AddressLookupVerification);
            goToStep(2);
          }}
          onLookupComplete={(hasMatches) => {
            setExistingRegistrationMatchFound(hasMatches);
            if (!hasMatches) {
              setStep(NewLicenseStep.LicenseTypeQuestions);
              goToStep(4);
            }
          }}
          onSelectTransferLicense={selectTransferLicense}
          onContinue={(mode, license) => {
            selectTransferLicense(mode === "transfer" ? license : null);
            setStep(NewLicenseStep.LicenseTypeQuestions);
            goToStep(4);
          }}
        />
      ) : null}

      {step === NewLicenseStep.LicenseTypeQuestions &&
        questionsData &&
        licenseType && (
          <div className="space-y-4">
            <LicenseTypeQuestionsForm
              onPrevious={async () => {
                if (licenseType?.requires_property) {
                  if (existingRegistrationMatchFound) {
                    setStep(NewLicenseStep.ExistingRegistration);
                    goToStep(3);
                  } else {
                    setStep(NewLicenseStep.AddressLookupVerification);
                    goToStep(2);
                  }
                } else {
                  setStep(NewLicenseStep.AddressDetails);
                  previousStep();
                }
              }}
              onNext={async (answers) => {
                setLicenseAnswers(
                  transferEffectiveDate
                    ? { ...answers, effective_date: transferEffectiveDate }
                    : answers,
                );
                setStep(NewLicenseStep.PeriodsAndFees);
                handleNextStep();
              }}
              licenseType={licenseType}
              initialAnswers={licenseAnswers ?? undefined}
              stepNumber={stepNumber}
              isRenewal={false}
              questionsData={questionsData}
              lockEffectiveDate={transferEffectiveDate != null}
            />
          </div>
        )}

      {licenseType &&
        jurisdiction &&
        feesData &&
        configurationData &&
        geocodedAddress &&
        geocodedLatitude &&
        geocodedLongitude &&
        licenseAnswers &&
        step === NewLicenseStep.PeriodsAndFees && (
          <div className="space-y-4">
            {transferFromLicenseId ? (
              <TransferOutstandingResponsibility
                jurisdictionOmt={configurationData.omt}
                questions={questionsData?.questions}
                originalOmtAnswer={transferOriginalOmtAnswer}
                currentAnswers={licenseAnswers}
                value={transferPassPriorUnpaid}
                onChange={setTransferPassPriorUnpaid}
              />
            ) : null}
            <PeriodsAndFeesForm
              inspectionExemptionAnswer={inspectionExemptionAnswer}
              effectiveDate={licenseAnswers?.effective_date}
              onPrevious={async () => {
                setStep(NewLicenseStep.LicenseTypeQuestions);
                handlePreviousStep();
              }}
              onSubmit={handleSubmit}
              stepNumber={stepNumber}
              isRenewal={false}
              feesData={feesData}
              configurationData={configurationData}
              effectiveDateLabel={licenseType.effective_date_label}
              paymentScreenQuestions={questionsData?.questions}
              paymentScreenAnswers={licenseAnswers ?? undefined}
            />
          </div>
        )}
    </div>
  );
}
